import { useNavigate } from 'react-router-dom'

import {
  EmployeeEditInBulkPageDocument,
  EmployeeEditInBulkPageQuery,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import hasFeature from 'modules/core/lib/hasFeature'

import useLegalEntities from 'modules/employees/hooks/useLegalEntities'
import type Employee from 'modules/employees/store/models/Employee'

import ListEmployees from './components/_ListEmployees'

interface Props {
  filtersForm: FiltersForm
  employees: Employee[]
}

type EditInBulk = EmployeeEditInBulkPageQuery['employees']['employees'][number]

const EditInBulk = ({ filtersForm, employees }: Props) => {
  const { legalEntities, loading } = useLegalEntities()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const includeCustomFields = hasFeature('custom_fields_in_edit_in_bulk')
  const { data, loading: loadingEmployeesInBulk } = useQuery(EmployeeEditInBulkPageDocument, {
    variables: { ids: employees.map((employee) => employee.id), includeCustomFields },
    fetchPolicy: 'network-only',
  })
  const canSeeAdminWelcomeSpace = Boolean(data?.employees._policies.seeAdminWelcomeSpace.can)
  const bulkEditableEmployeesIds = data?.employees.employees
    .filter((employee: EditInBulk) => employee._policies?.editInBulk.can)
    .map((employee: EditInBulk) => employee.id)
  const editableInBulkEmployees = employees.filter((employee) =>
    bulkEditableEmployeesIds?.includes(employee.id)
  )
  const canEditSelectedEmployeesInBulk = editableInBulkEmployees.length > 0
  const employeeGroups = data?.employees.employeeGroups || []
  const customFields = data?.customFields.fieldsConnection?.nodes || []
  const customFieldsValues = data?.customFields.valuesConnection?.nodes || []
  const sortedCustomFields = sortBy(customFields, 'position')

  useEffect(() => {
    track(trackingEvents.editEmployeesInBulk.startBulkEdit)
  }, [])

  if (loading || loadingEmployeesInBulk) return <Loading />

  if (!canEditSelectedEmployeesInBulk) {
    navigate({
      pathname: navigation.employees.fullPath(),
      search: `?can-edit-selected-employees=false`,
    })
  }

  return (
    <ErrorBoundary>
      <ListEmployees
        filtersForm={filtersForm}
        employees={editableInBulkEmployees}
        legalEntities={legalEntities}
        canSeeAdminWelcomeSpace={canSeeAdminWelcomeSpace}
        employeeGroups={employeeGroups}
        customFields={sortedCustomFields}
        customFieldsValues={customFieldsValues}
      />
    </ErrorBoundary>
  )
}

export default observer(EditInBulk)
